import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "redux/store";
import { api } from "api/axiosInterceptor";
import { apiUrl } from "api/apiUrl";

import {
  decrementRequests,
  incrementRequests,
  setIsLoading,
  setShowToast,
} from "./application";
import {
  IRfpState,
  RfpBudget,
  RfpQuestion,
  RfpWeight,
  RowData,
  UserData,
} from "types/rfp";
import { convertToCamelCase, openNotificationWithIcon } from "utils/utils";

const initialState: IRfpState = {
  rfpQuestion: {
    questions: {},
    extraction_categories: [],
    extraction_sub_categories: [],
  },
  rfpWeight: {
    extraction_categories: [],
  },
  autoFillWeight: {
    extraction_categories: [],
  },
  budget: {
    totalBudget: 0,
    rfpType: {
      costToEntity: false,
      revenueGenerating: false
    },
    message: ""
  },
  isFetchingCategories: false,
  rfpEvalRank: {},
  userList: [],
  roles: [],
  mailWithRole: [],
  memo: {},
};

const slice = createSlice({
  name: "overview",
  initialState,
  reducers: {
    rfpQuestionExtraction(state, action: PayloadAction<RfpQuestion>) {
      state.rfpQuestion = action.payload;
    },
    rfpEvalCriteria(state, action: PayloadAction<Record<string, string>>) {
      state.rfpEvalRank = action.payload;
    },
    getRfpCategoriesStart(state) {
      state.isFetchingCategories = true;
    },
    rfpCategories(state, action) {
      state.rfpWeight = action.payload;
      state.isFetchingCategories = false;
    },
    getRfpCategoriesFailure(state) {
      state.isFetchingCategories = false;
    },
    rfpAutoFillWeight(state, action: PayloadAction<RfpWeight>) {
      state.autoFillWeight = action.payload;
    },
    rfpBudget(state, action: PayloadAction<RfpBudget>) {
      state.budget = action.payload;
    },
    getInviteUserList(
      state,
      action: PayloadAction<{
        users: UserData[];
        role: string[];
      }>
    ) {
      const { users, role } = action.payload;
      state.userList = users;
      state.roles = role;
    },
    setStakeholderMails(
      state,
      action: PayloadAction<
        {
          email: string;
          role: string;
        }[]
      >
    ) {
      state.mailWithRole = action.payload;
    },
    setMemo(state, action: PayloadAction<Record<string, string>>) {
      state.memo = action.payload;
    },
  },
});

export const {
  rfpQuestionExtraction,
  rfpCategories,
  rfpBudget,
  rfpAutoFillWeight,
  rfpEvalCriteria,
  getRfpCategoriesFailure,
  getRfpCategoriesStart,
  getInviteUserList,
  setStakeholderMails,
  setMemo,
} = slice.actions;

export default slice.reducer;

export function getRfpQuestions() {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/rfp/question/extraction/`;
      const { status, data } = await api.get(path);
      dispatch(rfpQuestionExtraction(data));
      return { status, data };
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function getInviteStakeholders() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `${apiUrl.stakeHolder}records/`;
      const { data, status } = await api.get(path);
      if (status === 200) {
        dispatch(getInviteUserList(data));
      }
    } catch {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function sendInvitation(
  rfpId: number,
  data: { email: string; role: string }[],
  flow2 = false
) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = flow2
        ? `${apiUrl.stakeHolder}flow2/invite/${rfpId}/`
        : `${apiUrl.stakeHolder}invite/${rfpId}/`;

      const response = await api.post(path, {
        data,
      });
      if (response.status === 200) {
        dispatch(setStakeholderMails([]));
      }
      return response.status;
    } catch (err) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
      return err;
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function deleteInvitee(email: string, rfpId: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `${apiUrl.stakeHolder}remove/${rfpId}`;
      const mail = {
        email,
      };
      const { status } = await api.delete(path, {
        headers: {
          "Content-Type": "application/json",
        },
        data: mail,
      });

      if (status === 200) {
        openNotificationWithIcon(
          "success",
          "User removed successfully.",
          `User removed successfully from the invitees list.`
        );
      }

      return status;
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Something went wrong!",
        `Something went wrong while removing the user from the invitee list.`
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function setRfpQuestions(data: Record<number, RowData>) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/rfp/question/extraction/`;
      const response = await api.post(path, data);
      dispatch(rfpQuestionExtraction(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function getRfpCategories() {
  return async (dispatch: AppDispatch) => {
    dispatch(getRfpCategoriesStart());
    try {
      const path = `/rfp/question/categories/`;
      const response = await api.get(path);
      dispatch(rfpCategories(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
      dispatch(getRfpCategoriesFailure());
    }
  };
}

export function getRfpAutoFillWeight(data: RfpWeight) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/autofill/weight/`;
      const response = await api.post(path, data);
      dispatch(rfpAutoFillWeight(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function setRfpCategories(data: any) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/rfp/question/categories/`;
      const response = await api.post(path, data);
      // dispatch(rfpCategories(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function getRfpBudgets() {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `rfp/budget/`;
      const response = await api.get(path);
      const formattedResponse = convertToCamelCase(response.data);
      dispatch(rfpBudget(formattedResponse));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function setRfpBudgets(totalBudget: RfpBudget) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `rfp/budget/`;
      const response = await api.post(path, {
        total_budget: totalBudget.totalBudget,
      });
      dispatch(rfpBudget(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getEvaluationCriteria() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `rfp/evaluation/criteria/`;
      const response = await api.get(path);
      dispatch(rfpEvalCriteria(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function setEvaluationCriteria(data: any) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `rfp/evaluation/criteria/`;
      const response = await api.post(path, data);
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function generateMemo() {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const { rfp, memo } = apiUrl;
      const path = `${rfp}/${memo}`;
      const { data, status } = await api.get(path);
      if (status === 200) {
        dispatch(setMemo(data.rfp_memo));
      }
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong while generating memo!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}
